import React from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, Sparkles, XCircle } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = React.useState("checking"); // checking | paid | failed

  usePageMeta({
    title: "Payment confirmed | CV AI PRO",
    description: "Your payment was successful.",
    path: "/payment/success",
  });

  React.useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      while (!cancelled && attempts < 20) {
        attempts += 1;
        try {
          const { data } = await axios.get(`${API}/payments/status/${sessionId}`, {
            withCredentials: true,
          });
          if (data.payment_status === "paid") {
            if (!cancelled) setState("paid");
            return;
          }
          if (data.payment_status === "failed" || data.status === "expired") {
            if (!cancelled) setState("failed");
            return;
          }
        } catch (_) {
          // keep polling — webhook may just be slow
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setState((s) => (s === "checking" ? "failed" : s));
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="relative min-h-screen bg-black text-white" data-testid="payment-success-page">
      <div
        aria-hidden
        className="absolute -top-32 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-emerald-500/15 blur-[140px]"
      />
      <div aria-hidden className="absolute inset-0 grid-bg" />

      <div className="relative mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 pt-32 sm:pt-40 pb-24 text-center">
        {state === "checking" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="payment-checking">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-zinc-400" />
            <h1 className="mt-8 text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
              Confirming your payment…
            </h1>
            <p className="mt-4 text-zinc-400">This only takes a few seconds.</p>
          </motion.div>
        )}

        {state === "paid" && (
          <>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30"
            >
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="mt-8 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white text-balance"
              data-testid="payment-success-headline"
            >
              Payment confirmed.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18, duration: 0.6 }}
              className="mt-5 max-w-xl mx-auto text-zinc-400 text-balance"
            >
              Your AI resume credit is ready. Upload your current CV and get your
              optimized version in minutes — not days.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.6 }}
              className="mt-10"
            >
              <Link
                to="/upload"
                className="group inline-flex items-center gap-2 rounded-full bg-white text-black px-7 py-3.5 text-sm font-medium hover:bg-zinc-200 transition-colors"
                data-testid="payment-success-cta"
              >
                <Sparkles className="h-4 w-4" />
                Create my AI resume now
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          </>
        )}

        {state === "failed" && (
          <>
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/15 border border-amber-500/30">
              <XCircle className="h-8 w-8 text-amber-400" />
            </div>
            <h1
              className="mt-8 text-3xl sm:text-4xl font-semibold tracking-tighter text-white text-balance"
              data-testid="payment-pending-headline"
            >
              We're still confirming your payment.
            </h1>
            <p className="mt-5 max-w-xl mx-auto text-zinc-400 text-balance">
              If you completed the payment, it may take a minute to register. Check
              your account in a moment — or contact us if it doesn't show up.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 items-center justify-center">
              <Link
                to="/account"
                className="inline-flex items-center gap-2 rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors"
                data-testid="payment-pending-account"
              >
                Go to my account
              </Link>
              <a
                href="mailto:getcvaipro@gmail.com?subject=Payment%20issue"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors"
                data-testid="payment-pending-support"
              >
                Contact support
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
